import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';

type CommandPaletteContextValue = {
	isOpen: boolean;
	open: () => void;
	close: () => void;
	toggle: () => void;
};

const CommandPaletteContext = createContext<CommandPaletteContextValue | null>(null);

export const CommandPaletteProvider = ({ children }: { children: ReactNode }) => {
	const [isOpen, setIsOpen] = useState(false);

	const open = useCallback(() => setIsOpen(true), []);
	const close = useCallback(() => setIsOpen(false), []);
	const toggle = useCallback(() => setIsOpen((v) => !v), []);

	useEffect(() => {
		const handler = (e: KeyboardEvent) => {
			const isModK = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k';

			if (isModK) {
				e.preventDefault();
				toggle();
			} else if (e.key === 'Escape' && isOpen) {
				e.preventDefault();
				close();
			}
		};

		window.addEventListener('keydown', handler);
		return () => window.removeEventListener('keydown', handler);
	}, [toggle, close, isOpen]);

	const value = useMemo(() => ({ isOpen, open, close, toggle }), [isOpen, open, close, toggle]);

	return <CommandPaletteContext.Provider value={value}>{children}</CommandPaletteContext.Provider>;
};

export const useCommandPalette = () => {
	const ctx = useContext(CommandPaletteContext);

	if (!ctx) throw new Error('useCommandPalette must be used within CommandPaletteProvider');
	return ctx;
};
